import React, { useState } from "react";
import { toDataURL } from "qrcode";
import { useLazyQuery, useMutation, gql } from "@apollo/client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  OtpAuthUriQuery,
  OtpAuthUriQueryVariables,
  EnableMfaMutation,
  EnableMfaMutationVariables,
} from "@/__generated__/graphql";

const OTP_AUTH_URI = gql`
  query OtpAuthUri {
    mfaOtpAuthUri
  }
`;

const ENABLE_MFA = gql`
  mutation EnableMfa($verificationCode: String!) {
    enableMfa(verificationCode: $verificationCode) {
      success
      message
    }
  }
`;

const EnableMFAForm = () => {
  const [error, setError] = useState<string | null | undefined>(null);
  const [showMfaForm, setShowMfaForm] = useState(false);
  const [verificationCode, setVerificationCode] = React.useState<string>("");
  const [qrCodeDataUrl, setQrCodeDataUrl] = React.useState<string | null>(null);

  const [getOtpAuthUri, { loading }] = useLazyQuery<
    OtpAuthUriQuery,
    OtpAuthUriQueryVariables
  >(OTP_AUTH_URI, {
    fetchPolicy: "network-only",
    onCompleted: async (data) => {
      if (data.mfaOtpAuthUri) {
        const url = await toDataURL(data.mfaOtpAuthUri);
        setQrCodeDataUrl(url);
      }
    },
  });

  const [enableMfa] = useMutation<
    EnableMfaMutation,
    EnableMfaMutationVariables
  >(ENABLE_MFA);

  return (
    <div className="flex flex-col text-sm gap-1 mb-2">
      <h5 className="text-lg font-medium text-secondary-foreground">
        Multi-factor Authentication
      </h5>
      {!showMfaForm ? (
        <div className="flex flex-row justify-between items-center h-[20px]">
          <p className="text-base">Enable mutifactor authentication</p>
          <Button
            variant="paint"
            onClick={() => {
              getOtpAuthUri();
              setShowMfaForm(true);
            }}
          >
            Enable now
          </Button>
        </div>
      ) : (
        !loading &&
        qrCodeDataUrl && (
          <div className="flex flex-col gap-5">
            <p className="text-base text-secondary-foreground">
              Use an authenticator app like Google Authenticator or Authy to
              scan the QR ccode below
            </p>
            <div className="h-45 w-full flex items-center justify-center">
              <img src={qrCodeDataUrl} alt="QR Code" className="h-full" />
            </div>
            <div className="flex flex-col gap-1">
              {error && <div className="text-[#F04438] text-sm">{error}</div>}
              <div className="flex flex-row justify-between gap-3">
                <Input
                  placeholder="Enter the code from authenticator"
                  className="border-none bg-accent dark:bg-accent placeholder:text-base placeholder:text-muted-foreground"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                />
                <div className="flex flex-row gap-2">
                  <Button
                    variant="paint"
                    onClick={async () => {
                      const result = await enableMfa({
                        variables: { verificationCode },
                      });
                      if (result && result.data && result.data.enableMfa) {
                        const { success, message } = result.data.enableMfa;
                        if (success) {
                          setShowMfaForm(false);
                        } else {
                          setError(message);
                        }
                      }
                    }}
                  >
                    Submit
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowMfaForm(false);
                    }}
                    className="text-sm gap-1 items-center hover:bg-accent !text-primary-foreground"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default EnableMFAForm;
